// RECENCY MEASUREMENT — observe-only. Counts how often two different systems
// (D&D Beyond, Roll20, DM Screen) change the SAME PC field around the same time,
// and how close together. This is the "measure before you design" pass for the
// cross-system override-hold: the conflict-resolution UX must be designed to the
// REAL rate (an AoE on four PCs vs. a once-a-session edge), not a guess.
//
// IT CHANGES NOTHING. It gates no apply and writes nothing outward — every caller
// records an observation and moves on. That is what makes it safe to run on a live
// prod game, which is the only place the real rate exists. Read it back with
// `window.__recencyMetrics.summary()` (or it persists to localStorage per session).

export type MetricSource = "ddb" | "roll20" | "dms";

/** Human labels for the three systems, for a note shown to the DM. */
export const SOURCE_LABEL: Record<MetricSource, string> = { ddb: "D&D Beyond", roll20: "Roll20", dms: "DM Screen" };

/** The co-authored scalar fields this first pass measures. Scalars only, so the
 *  value compares cleanly across sources (String()); conditions (objects with
 *  durations) are deferred — the question that matters is the HP/AoE conflict rate. */
export const OBSERVE_FIELDS = ["hp", "tempHp", "maxHp", "exhaustion", "deathSuccesses", "deathFailures"] as const;

/** A cross-source change beyond this gap is "sequential", not a conflict. The poll
 *  period is ~30 s, so that is the natural outer bound for "around the same time". */
const CONFLICT_WINDOW_MS = 30000;
/** Delta buckets (ms) for the distribution — how SIMULTANEOUS conflicts really are. */
const BUCKETS = [1000, 5000, 15000, 30000];
const STORE_KEY = "dm-screen-recency-metrics";

interface LastChange { source: MetricSource; value: string; at: number }
interface Metrics {
  startedAt: number;
  observations: number;
  byObserveSource: Record<string, number>;
  /** cross-source, same field, within the window, DIFFERENT value — a real conflict. */
  conflicts: number;
  /** cross-source, same field, within the window, SAME value — propagation/echo, not a conflict. */
  echoes: number;
  conflictsByField: Record<string, number>;
  conflictsBySourcePair: Record<string, number>;   // "ddb|roll20" etc. (sorted)
  deltaBuckets: Record<string, number>;             // "<1s","<5s","<15s","<30s"
  sameSecond: number;                               // conflicts within 1 s — the true-simultaneous case
  /** a sample of recent conflicts, for eyeballing (bounded). */
  recent: { at: string; field: string; a: MetricSource; b: MetricSource; was: string; now: string; deltaMs: number }[];
}

const fresh = (): Metrics => ({
  startedAt: Date.now(), observations: 0, byObserveSource: {}, conflicts: 0, echoes: 0,
  conflictsByField: {}, conflictsBySourcePair: {}, deltaBuckets: {}, sameSecond: 0, recent: [],
});

const last = new Map<string, Map<string, LastChange>>();   // id -> field -> last change
// id -> field -> source -> that source's last observed value. Unlike `last` (which
// keeps only the single most-recent change per field, whatever its source), this
// retains each system's latest value side by side, so we can ask "do two systems
// hold DIFFERENT values for this field right now?" — the signal the concentration
// card uses to warn that a drop may be a cross-source sync, not real damage.
// Observe-only, like everything in this module.
const lastBySource = new Map<string, Map<string, Map<MetricSource, LastChange>>>();
let m: Metrics = load();

function load(): Metrics {
  try { const raw = localStorage.getItem(STORE_KEY); if (raw) return { ...fresh(), ...JSON.parse(raw) }; } catch { /* private mode / cleared */ }
  return fresh();
}
function persist(): void {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(m)); } catch { /* best effort */ }
}
const bucketOf = (deltaMs: number): string => {
  for (const b of BUCKETS) if (deltaMs < b) return "<" + (b / 1000) + "s";
  return ">=" + (BUCKETS[BUCKETS.length - 1] / 1000) + "s";
};

/** Record that `source` changed (id, field) to `value` at `at`. Observe-only. */
export function observe(id: string, field: string, source: MetricSource, value: unknown, at: number = Date.now()): void {
  m.observations++;
  m.byObserveSource[source] = (m.byObserveSource[source] || 0) + 1;
  const v = value === undefined || value === null ? "" : String(value);
  let byField = last.get(id);
  if (!byField) { byField = new Map(); last.set(id, byField); }
  const prev = byField.get(field);
  byField.set(field, { source, value: v, at });
  // Retain this source's latest value alongside the other sources' (see lastBySource).
  let bySrc = lastBySource.get(id);
  if (!bySrc) { bySrc = new Map(); lastBySource.set(id, bySrc); }
  let srcMap = bySrc.get(field);
  if (!srcMap) { srcMap = new Map(); bySrc.set(field, srcMap); }
  srcMap.set(source, { source, value: v, at });
  if (!prev || prev.source === source) { persist(); return; }      // first sight, or same source → not cross-source
  const delta = Math.abs(at - prev.at);
  if (delta > CONFLICT_WINDOW_MS) { persist(); return; }            // too far apart → sequential, not a conflict
  if (prev.value === v) { m.echoes++; persist(); return; }          // same value → propagation/echo, not a conflict
  // a real cross-source conflict within the window
  m.conflicts++;
  m.conflictsByField[field] = (m.conflictsByField[field] || 0) + 1;
  const pair = [prev.source, source].sort().join("|");
  m.conflictsBySourcePair[pair] = (m.conflictsBySourcePair[pair] || 0) + 1;
  const bkt = bucketOf(delta);
  m.deltaBuckets[bkt] = (m.deltaBuckets[bkt] || 0) + 1;
  if (delta < 1000) m.sameSecond++;
  m.recent.unshift({ at: new Date(at).toISOString(), field, a: prev.source, b: source, was: prev.value.slice(0, 40), now: v.slice(0, 40), deltaMs: delta });
  if (m.recent.length > 50) m.recent.length = 50;
  persist();
}

/** The accumulated summary (for console readback / a diagnostics line). */
export function summary(): Metrics & { sessionMinutes: number } {
  return { ...m, sessionMinutes: Math.round((Date.now() - m.startedAt) / 60000) };
}

/** Start a fresh measurement window (e.g. at the start of a session being measured). */
export function resetMetrics(): void { last.clear(); lastBySource.clear(); m = fresh(); persist(); }

/** The two systems and their values when (id, field) is held differently by two
 *  sources right now — what the concentration card warns on. Observe-only. */
export interface SourceDisagreement { a: MetricSource; aValue: string; b: MetricSource; bValue: string; deltaMs: number }

/** Do two different systems currently hold DIFFERENT values for (id, field)?
 *  `lastBySource` has each system's latest observed value; this reports the pair
 *  worth warning the DM about, or null when there is nothing to flag. Observe-only. */
export function sourceDisagreement(id: string, field: string, withinMs: number = CONFLICT_WINDOW_MS): SourceDisagreement | null {
  const bySrc = lastBySource.get(id)?.get(field);
  if (!bySrc || bySrc.size < 2) return null;   // need at least two systems' values to disagree
  // Report the NEWEST observation against the most-recent OTHER source whose value
  // differs, but only while the two were seen within `withinMs` of each other — a
  // long-stale read from a system nobody has touched is not a live disagreement.
  // A matching value across sources is propagation (an "echo"), not a conflict.
  const seen = [...bySrc.values()].sort((x, y) => y.at - x.at);   // newest first
  const newest = seen[0];
  for (let i = 1; i < seen.length; i++) {
    const other = seen[i];
    if (other.value === newest.value) continue;        // same value → echo, not a conflict
    const deltaMs = Math.abs(newest.at - other.at);
    if (deltaMs > withinMs) continue;                  // too far apart → stale, don't cry wolf
    return { a: newest.source, aValue: newest.value, b: other.source, bValue: other.value, deltaMs };
  }
  return null;
}

// Console readback on a live game: `window.__recencyMetrics.summary()`. Guarded so
// it is a no-op under SSR (the render-smoke test) and never touches a missing window.
try {
  if (typeof window !== "undefined") {
    (window as unknown as { __recencyMetrics?: unknown }).__recencyMetrics = { summary, resetMetrics };
  }
} catch { /* no window */ }
